import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../context/AuthContext";
import { Plus, Pencil, Trash2, Check, X, Save, RefreshCw } from "lucide-react";
import { FILM_LENGTHS, FILM_GENRES } from "../../constants/film";

const STATUSES = ["all", "published", "pending", "rejected"];
const emptyFilm = { title: "", director: "", description: "", youtube_id: "", year: new Date().getFullYear(), duration: "", length: "Short", genres: [], tags: "" };

export default function AdminFilms() {
  const [films, setFilms] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const r = await axios.get(`${API}/admin/films`, { withCredentials: true });
      setFilms(r.data || []);
    } catch (e) {
      setError("Could not load films.");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => statusFilter === "all" ? films : films.filter(f => f.status === statusFilter), [films, statusFilter]);
  const counts = useMemo(() => ({
    published: films.filter(f => f.status === "published").length,
    pending: films.filter(f => f.status === "pending").length,
    rejected: films.filter(f => f.status === "rejected").length,
  }), [films]);

  return (
    <div data-testid="admin-tab-films">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading font-black text-[#0A0A0A] text-2xl tracking-tight">Films programme</h2>
          <p className="text-sm text-[#2C2C2C]">Approve submissions and curate the public list.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={busy} data-testid="admin-films-refresh" className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button onClick={() => setShowAdd(true)} data-testid="admin-films-add" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] pop-shadow rounded-none px-5 py-2 text-sm font-medium inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add film</button>
        </div>
      </div>

      {error && <div className="bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 mb-4" role="alert">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Total" value={films.length} bg="bg-[#FFD93D]" />
        <Stat label="Published" value={counts.published} bg="bg-[#1A5DFF]" text="text-[#FFF8E7]" />
        <Stat label="Pending" value={counts.pending} bg="bg-[#FF2E63]" text="text-[#FFF8E7]" />
        <Stat label="Rejected" value={counts.rejected} bg="bg-white" />
      </div>

      <div className="flex flex-wrap gap-2 mb-5" role="tablist" data-testid="admin-films-status-filters">
        {STATUSES.map(s => (
          <button key={s} role="tab" aria-selected={s === statusFilter} onClick={() => setStatusFilter(s)} data-testid={`admin-films-filter-${s}`} className={`px-4 py-2 text-sm font-medium border-2 transition-colors ${s === statusFilter ? "bg-[#0A0A0A] text-[#FFF8E7] border-[#0A0A0A]" : "bg-transparent text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#FFD93D]"}`}>{s[0].toUpperCase()+s.slice(1)}</button>
        ))}
      </div>

      <div className="space-y-3" data-testid="admin-films-list">
        {filtered.map(f => <FilmRow key={f.id} film={f} onEdit={() => setEditing(f)} onChanged={load} />)}
        {!busy && filtered.length === 0 && <div className="text-[#2C2C2C]">No films in this view.</div>}
      </div>

      {showAdd && <FilmFormModal title="Add a new film" initial={emptyFilm} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} mode="create" />}
      {editing && <FilmFormModal title="Edit film" initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} mode="edit" />}
    </div>
  );
}

function Stat({ label, value, bg, text = "text-[#0A0A0A]" }) {
  return (
    <div className={`${bg} ${text} border-2 border-[#0A0A0A] p-4`}>
      <div className="text-xs uppercase tracking-[0.18em] font-bold">{label}</div>
      <div className="font-heading font-black text-3xl mt-1">{value}</div>
    </div>
  );
}

function FilmRow({ film, onEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const act = async (verb) => {
    if (verb === "delete" && !window.confirm(`Delete "${film.title}"?`)) return;
    setBusy(true);
    try {
      if (verb === "delete") await axios.delete(`${API}/admin/films/${film.id}`, { withCredentials: true });
      else await axios.post(`${API}/admin/films/${film.id}/${verb}`, {}, { withCredentials: true });
      onChanged();
    } catch { alert("Action failed."); } finally { setBusy(false); }
  };
  const statusBg = film.status === "published" ? "bg-[#1A5DFF] text-[#FFF8E7]" : film.status === "pending" ? "bg-[#FFD93D] text-[#0A0A0A]" : "bg-[#0A0A0A] text-[#FFF8E7]";
  return (
    <div className="bg-white border-2 border-[#0A0A0A] p-4 md:p-5 flex flex-col md:flex-row gap-4 items-start" data-testid={`admin-film-${film.id}`}>
      <img src={`https://i.ytimg.com/vi/${film.youtube_id}/default.jpg`} alt="" className="w-32 h-20 object-cover border-2 border-[#0A0A0A] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`text-xs uppercase tracking-[0.16em] font-bold px-2 py-0.5 border-2 border-[#0A0A0A] ${statusBg}`}>{film.status}</span>
          <span className="text-xs text-[#2C2C2C] uppercase tracking-[0.14em]">{film.category} · {film.year} · {film.duration}</span>
        </div>
        <h3 className="font-heading font-bold text-lg text-[#0A0A0A] leading-tight">{film.title}</h3>
        <div className="text-sm text-[#2C2C2C]">Dir. {film.director}</div>
        {film.submitter_email && <div className="text-xs text-[#2C2C2C] mt-1">Submitted by {film.submitter_name} ({film.submitter_email})</div>}
      </div>
      <div className="flex flex-wrap gap-2">
        {film.status !== "published" && <button disabled={busy} onClick={() => act("approve")} data-testid={`admin-approve-${film.id}`} className="bg-[#1A5DFF] text-[#FFF8E7] hover:bg-[#0E4ACC] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Approve</button>}
        {film.status !== "rejected" && <button disabled={busy} onClick={() => act("reject")} data-testid={`admin-reject-${film.id}`} className="bg-transparent text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> Reject</button>}
        <button disabled={busy} onClick={onEdit} data-testid={`admin-edit-${film.id}`} className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</button>
        <button disabled={busy} onClick={() => act("delete")} data-testid={`admin-delete-${film.id}`} className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
      </div>
    </div>
  );
}

function FilmFormModal({ title, initial, onClose, onSaved, mode }) {
  const [form, setForm] = useState({
    title: initial.title || "", director: initial.director || "", description: initial.description || "",
    youtube_id: initial.youtube_id || "", year: initial.year || new Date().getFullYear(),
    duration: initial.duration || "",
    length: initial.length || "Short",
    genres: Array.isArray(initial.genres) ? initial.genres : [],
    tags: Array.isArray(initial.tags) ? initial.tags.join(", ") : (initial.tags || ""),
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleGenre = (g) => set("genres", form.genres.includes(g) ? form.genres.filter(x => x !== g) : [...form.genres, g]);

  const save = async (e) => {
    e.preventDefault(); setError(null); setSubmitting(true);
    try {
      const payload = { ...form, year: Number(form.year), tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) };
      if (mode === "create") await axios.post(`${API}/admin/films`, payload, { withCredentials: true });
      else await axios.put(`${API}/admin/films/${initial.id}`, payload, { withCredentials: true });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A]/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="film-form-modal">
      <form onClick={e => e.stopPropagation()} onSubmit={save} className="bg-[#FFF8E7] border-2 border-[#0A0A0A] pop-shadow max-w-3xl w-full p-8 my-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF2E63] mb-1">{mode === "create" ? "New film" : "Edit"}</div>
            <h2 className="font-heading font-black text-[#0A0A0A] text-2xl">{title}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="film-form-close" className="bg-[#0A0A0A] text-[#FFF8E7] hover:bg-[#FF2E63] p-2 border-2 border-[#0A0A0A]"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AdminField label="Film title" required><input data-testid="admin-field-title" value={form.title} onChange={e => set("title", e.target.value)} required className={adminInputCls} /></AdminField>
          <AdminField label="Director" required><input data-testid="admin-field-director" value={form.director} onChange={e => set("director", e.target.value)} required className={adminInputCls} /></AdminField>
          <AdminField label="YouTube URL or video ID" required full hint="Paste full URL or just the 11-char ID"><input data-testid="admin-field-youtube" value={form.youtube_id} onChange={e => set("youtube_id", e.target.value)} required className={adminInputCls} /></AdminField>
          <AdminField label="Year" required><input data-testid="admin-field-year" type="number" min="1900" max="2100" value={form.year} onChange={e => set("year", e.target.value)} required className={adminInputCls} /></AdminField>
          <AdminField label="Duration" required><input data-testid="admin-field-duration" value={form.duration} onChange={e => set("duration", e.target.value)} placeholder="MM:SS" required className={adminInputCls} /></AdminField>
          <AdminField label="Length" required full hint="Format of the film — not its genre">
            <div className="flex flex-wrap gap-2" role="radiogroup" data-testid="admin-field-length">
              {FILM_LENGTHS.map((l) => {
                const active = form.length === l;
                return (
                  <button key={l} type="button" role="radio" aria-checked={active} onClick={() => set("length", l)} className={`px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] border-2 border-[#0A0A0A] ${active ? "bg-[#FF2E63] text-[#FFF8E7]" : "bg-white text-[#0A0A0A] hover:bg-[#FFD93D]"}`}>{l}</button>
                );
              })}
            </div>
          </AdminField>
          <AdminField label="Genres" full hint="Tick all that apply">
            <div className="flex flex-wrap gap-2" role="group" data-testid="admin-field-genres">
              {FILM_GENRES.map((g) => {
                const active = form.genres.includes(g);
                return (
                  <button key={g} type="button" aria-pressed={active} onClick={() => toggleGenre(g)} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] border-2 border-[#0A0A0A] ${active ? "bg-[#1A5DFF] text-[#FFF8E7]" : "bg-white text-[#0A0A0A] hover:bg-[#FFD93D]"}`}>{g}</button>
                );
              })}
            </div>
          </AdminField>
          <AdminField label="Tags (comma-separated)" full hint="Optional — e.g. autism, coming-of-age"><input data-testid="admin-field-tags" value={form.tags} onChange={e => set("tags", e.target.value)} className={adminInputCls} /></AdminField>
          <AdminField label="Description" required full><textarea data-testid="admin-field-description" rows={4} value={form.description} onChange={e => set("description", e.target.value)} required className={adminInputCls} /></AdminField>
        </div>
        {error && <div className="mt-4 bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 text-sm">{error}</div>}
        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-5 py-2.5 text-sm font-medium">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="admin-save-film" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] disabled:opacity-60 border-2 border-[#0A0A0A] pop-shadow rounded-none px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"><Save className="w-4 h-4" /> {submitting ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

const adminInputCls = "w-full bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none text-sm";

function AdminField({ label, hint, children, required, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-[0.16em] font-bold text-[#0A0A0A]">{label} {required && <span className="text-[#FF2E63]">*</span>}</span>
      {children}
      {hint && <span className="text-xs text-[#2C2C2C]">{hint}</span>}
    </label>
  );
}
